import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

export default function Programs() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [programs, setPrograms] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [params] = useSearchParams();
  const preselect = params.get("select");

  useEffect(() => {
    client.get("/programs").then(r => setPrograms(r.data || []));
  }, []);

  const subscribe = async (program_id) => {
    setLoadingId(program_id);
    try {
      const { data } = await client.post("/payments/checkout", {
        program_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoadingId(null);
    }
  };

  const subscribePayPal = async (program_id) => {
    setLoadingId(`pp-${program_id}`);
    try {
      const { data } = await client.post("/payments/paypal/create-order", {
        program_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal checkout failed");
      setLoadingId(null);
    }
  };

  useEffect(() => { if (preselect) document.getElementById(`prog-${preselect}`)?.scrollIntoView({ behavior: "smooth" }); }, [preselect, programs]);

  const active = user?.active_programs || [];

  return (
    <div data-testid="programs-page" className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Programs</div>
      <h1 className="font-display text-3xl md:text-5xl font-bold mt-2">Activate your AI program</h1>
      <p className="mt-3 text-white/60 max-w-2xl">Pay once with USDC or card. Programs are non-refundable. You provide your own exchange keys.</p>

      <div className="mt-10 grid md:grid-cols-3 gap-px bg-[#222] border border-[#222]">
        {programs.map((p, i) => {
          const isPremium = p.id === "pepite";
          const isActive = active.includes(p.id);
          const title = p.id === "basic" ? t("program_basic") : p.id === "memecoins" ? t("program_memecoins") : t("program_pepite");
          return (
            <div id={`prog-${p.id}`} key={p.id} className={`bg-[#0A0A0A] p-8 relative ${isPremium ? "ring-1 ring-[#CCFF00]/30" : ""}`} data-testid={`programs-card-${p.id}`}>
              {isActive && (
                <div className="absolute top-4 right-4 bg-[#CCFF00] text-black text-[10px] font-mono uppercase tracking-widest px-2 py-1 flex items-center gap-1">
                  <Check size={10} /> {t("program_active")}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Program {String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-2xl font-bold mt-3">{title}</h3>
              <p className="text-white/50 text-sm mt-3 leading-relaxed">{p.description}</p>

              <div className="mt-8 border-t border-[#222] pt-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold">{Math.round(p.price_eur).toLocaleString()}</span>
                  <span className="text-white/40 font-mono text-sm">€</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mt-2">{t("one_time")}</div>
                <div className="mt-4 text-xs font-mono text-white/60">
                  {t("min_deposit")}: <span className="text-[#CCFF00]">{Math.round(p.min_deposit_eur).toLocaleString()}€</span>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-2">Assets</div>
                <div className="flex flex-wrap gap-2">
                  {p.assets.map(a => <span key={a} className="text-[10px] font-mono px-2 py-1 border border-[#222]">{a}</span>)}
                </div>
              </div>

              <div className="mt-6 border-t border-[#222] pt-4 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Performance fee</div>
                <div className="font-mono text-sm text-[#CCFF00]" data-testid={`programs-commission-${p.id}`}>
                  {p.commission_pct}% / winning trade
                </div>
              </div>

              <button
                onClick={() => subscribe(p.id)}
                disabled={isActive || loadingId === p.id}
                data-testid={`programs-subscribe-${p.id}`}
                className={`mt-8 w-full ${isPremium ? "btn-acid" : "btn-ghost"} flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {loadingId === p.id ? <Loader2 size={14} className="animate-spin" /> : null}
                {isActive ? t("program_active") : `${t("pay_crypto")} →`}
              </button>
              <button
                onClick={() => subscribePayPal(p.id)}
                disabled={isActive || loadingId === `pp-${p.id}`}
                data-testid={`programs-paypal-${p.id}`}
                className="mt-3 w-full btn-ghost flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loadingId === `pp-${p.id}` ? <Loader2 size={14} className="animate-spin" /> : null}
                {t("pay_paypal")}
              </button>
              <div className="mt-3 text-[10px] text-white/40 font-mono text-center">{t("pay_methods_note")}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
